// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Pop-up « Nouveautés » — affiché une fois par version notable, au login.
//
// MÉMORISATION : `localStorage` sous `norma_changelog_seen`, qui contient LA
// VERSION VUE, pas un booléen. Un booléen « déjà vu » est le piège classique :
// fermer 3.15.2 masquerait aussi 3.16. Arbitrage de Marc du 18/08/2026 —
// `localStorage` plutôt qu'une colonne en base : aucune migration, cohérent avec
// le thème et les analyses récentes déjà stockés ainsi. Contrepartie assumée :
// sur un autre navigateur, le pop-up reparaît une fois.
//
// `notable` : seules les versions marquantes interrompent le client. Un pop-up à
// chaque correctif finit fermé sans être lu.
import React, { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';
import { useLocation } from 'wouter';
import { useI18n } from '../i18n';
import { DERNIERE_VERSION } from '../data/changelog';

const LS_KEY = 'norma_changelog_seen';

const WhatsNewModal = () => {
  const { t, lang } = useI18n();
  const [, setLocation] = useLocation();
  const [ouvert, setOuvert] = useState(false);

  const version = DERNIERE_VERSION;

  useEffect(() => {
    if (!version?.notable) return;
    // Comparaison d'égalité, pas d'ordre : on ne cherche pas « plus récent que »
    // mais « ce n'est pas celle qu'il a fermée ». Un retour arrière de version
    // doit donc réafficher la note, ce qui est le comportement voulu.
    if (localStorage.getItem(LS_KEY) === version.version) return;
    setOuvert(true);
  }, [version]);

  // La fermeture est le SEUL geste qui marque la version comme vue — un clic à
  // côté ou une touche Échap ne doivent pas escamoter la note par accident.
  const fermer = () => {
    localStorage.setItem(LS_KEY, version.version);
    setOuvert(false);
  };

  const versLaPage = () => {
    fermer();
    setLocation('/nouveautes');
  };

  if (!ouvert || !version) return null;

  // Date formatée selon la locale du navigateur : la donnée est en ISO dans le
  // registre, jamais du texte figé en français.
  const [a, m, j] = version.date.split('-');
  const dateLisible = new Date(Number(a), Number(m) - 1, Number(j)).toLocaleDateString(
    lang === 'en' ? 'en-GB' : 'fr-FR',
    { year: 'numeric', month: 'long', day: 'numeric' }
  );

  const texte = (bloc) => bloc?.[lang] ?? bloc?.fr ?? '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="whatsnew-titre"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)', padding: 16,
      }}
    >
      <div style={{
        background: 'var(--bg-card, white)', color: 'var(--text-main, #1a1a1a)',
        borderRadius: 16, maxWidth: 620, width: '100%', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
      }}>

        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '20px 20px 12px 20px',
          borderBottom: '1px solid var(--border, #e5e7eb)',
        }}>
          <Sparkles size={20} style={{ color: 'var(--gold, #c9a227)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div id="whatsnew-titre" style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>
              {t('whatsnew_title')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)', marginTop: 4 }}>
              {t('whatsnew_version')} {version.version} · {dateLisible}
            </div>
          </div>
          <button
            onClick={fermer}
            aria-label={t('whatsnew_close')}
            title={t('whatsnew_close')}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted, #6b7280)', padding: 4, lineHeight: 0,
              borderRadius: 6, flexShrink: 0,
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
          <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 14px 0', lineHeight: 1.45 }}>
            {texte(version.titre)}
          </p>
          {/* Version courte si elle existe, sinon la complète : une version brève
              n'a pas à se dédoubler dans le registre. */}
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(version.entrees_popup ?? version.entrees).map((e, i) => (
              <li key={i} style={{ fontSize: 13, lineHeight: 1.5 }}>{texte(e)}</li>
            ))}
          </ul>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          padding: '12px 20px 16px 20px', borderTop: '1px solid var(--border, #e5e7eb)',
        }}>
          <button
            onClick={versLaPage}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--gold, #c9a227)', fontSize: 13, fontWeight: 500, padding: 0,
              textDecoration: 'underline',
            }}
          >
            {t('whatsnew_see_all')}
          </button>
          <button
            onClick={fermer}
            style={{
              background: 'var(--gold, #c9a227)', color: '#1a1a1a', border: 'none',
              cursor: 'pointer', borderRadius: 8, padding: '8px 18px',
              fontSize: 13, fontWeight: 600,
            }}
          >
            {t('whatsnew_got_it')}
          </button>
        </div>

      </div>
    </div>
  );
};

export default WhatsNewModal;
